import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ResponsiveLayoutService } from '../../../shared-services/responsive-layout.service';

/**
 * Owns the chart-builder's preview ECharts instance.
 *
 * Pulled out of SelfServiceVisualizationComponent so the ECharts lifecycle
 * (init / dispose / resize) lives in one small, focused place. The parent
 * computes the EChartsOption (via SelfServiceChartOptionBuilderService) and
 * passes it in; this component only renders it and keeps it sized.
 *
 * Re-renders via an effect() that reads the `option` signal input — NOT
 * ngOnChanges, which does not fire for signal inputs. Follows the project's
 * responsive pattern: resizes on ResponsiveLayoutService.reflow$.
 */
@Component({
  selector: 'app-chart-preview',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div #chartPreview class="chart-preview-host"></div>`,
  styles: [`
    :host { display: block; width: 100%; height: var(--dvm-chart-height, 460px); }
    .chart-preview-host { width: 100%; height: 100%; min-height: 420px; }
  `],
})
export class ChartPreviewComponent implements AfterViewInit, OnDestroy {
  /** The ECharts option to render. Null clears/hides the chart. */
  readonly option = input<echarts.EChartsOption | null>(null);

  @ViewChild('chartPreview', { static: false })
  private chartPreview?: ElementRef<HTMLDivElement>;

  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private reflowSub?: Subscription;
  private chart?: echarts.ECharts;
  private viewReady = false;
  private resizeObserver?: ResizeObserver;

  constructor() {
    // Re-render whenever the option signal changes. effect() tracks the signal
    // read; once the view is ready, every option update repaints the chart.
    effect(() => {
      const option = this.option();
      if (this.viewReady) {
        // Defer to a macrotask so the host has its real (post-CD) height.
        setTimeout(() => this.render(option), 0);
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.chart?.resize());
    // Re-fit the chart to its card whenever the host box changes size. In the
    // DVM builder the tile is laid out / drag-resized AFTER the chart inits, and
    // the reflow$ nudge alone doesn't cover those; observing the host keeps the
    // ECharts canvas exactly the card's size (no overflow / no empty gap).
    const host = this.chartPreview?.nativeElement;
    if (host && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(host);
    }
    // Initial paint with whatever option is already set.
    setTimeout(() => this.render(this.option()), 0);
  }

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
    this.chart = undefined;
  }

  private render(option: echarts.EChartsOption | null): void {
    // eslint-disable-next-line no-console
    console.log('[chart-preview] render() called', { hasHost: !!this.chartPreview?.nativeElement, option });
    const host = this.chartPreview?.nativeElement;
    if (!host) {
      return;
    }
    if (!option) {
      this.chart?.clear();
      return;
    }
    if (this.chart && this.chart.getDom() !== host) {
      this.chart.dispose();
      this.chart = undefined;
    }
    if (!this.chart) {
      this.chart = echarts.init(host);
    }
    this.chart.resize();
    this.chart.setOption(option, true);
    this.chart.resize();
  }
}
